import { useState } from 'react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/hooks/use-auth';
import { useToast } from '@/hooks/use-toast';
import { Turnstile } from '@marsidev/react-turnstile';
import { AlertCircle } from 'lucide-react';
import { Loader2 } from 'lucide-react';

const PASSWORD_REQUIREMENTS = {
  minLength: 12,
  pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*])/
};

const TURNSTILE_ENABLED = import.meta.env.VITE_ENABLE_TURNSTILE === 'true';

interface AuthDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

export function AuthDialog({ isOpen, onClose }: AuthDialogProps) {
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [signUpCaptchaToken, setSignUpCaptchaToken] = useState<string>('');
  const [signInCaptchaToken, setSignInCaptchaToken] = useState<string>('');
  const [authError, setAuthError] = useState<string>('');
  const { signIn, signUp } = useAuth();
  const { toast } = useToast();

  const validatePassword = (pass: string) => {
    if (pass.length < PASSWORD_REQUIREMENTS.minLength) {
      return `Password must be at least ${PASSWORD_REQUIREMENTS.minLength} characters long`;
    }
    if (!PASSWORD_REQUIREMENTS.pattern.test(pass)) {
      return 'Password must contain at least one lowercase letter, one uppercase letter, one number, and one special character';
    }
    return '';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setAuthError('');

    const passwordError = validatePassword(password);
    if (passwordError) {
      toast({
        title: "Invalid Password",
        description: passwordError,
        variant: "destructive"
      });
      setLoading(false);
      return;
    }

    try {
      const { error } = isSignUp 
        ? await signUp(email, password, TURNSTILE_ENABLED ? signUpCaptchaToken : '')
        : await signIn(email, password, TURNSTILE_ENABLED ? signInCaptchaToken : '');

      if (error) {
        if (error.message.includes('weak_password') || error.message.includes('known to be weak')) {
          setAuthError('This password is known to be weak and easy to guess. Please choose a different one.');
        } else {
          toast({
            title: "Authentication Error",
            description: error.message,
            variant: "destructive"
          });
        }
      } else {
        toast({
          title: isSignUp ? "Account Created" : "Welcome Back",
          description: isSignUp ? "Your account has been created successfully" : "You have been signed in successfully"
        });
        onClose();
      }
    } catch (err) {
      toast({
        title: "Error",
        description: "An unexpected error occurred",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
      setSignUpCaptchaToken('');
      setSignInCaptchaToken('');
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[440px] p-0 overflow-hidden bg-background-primary border-border">
        <div className="p-6">
          <DialogTitle className="text-xl font-semibold text-text-primary mb-6">
            {isSignUp ? 'Create an account' : 'Sign in'}
          </DialogTitle>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium text-text-secondary">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="h-8 px-3 bg-search-bg border-border text-text-primary placeholder-text-secondary focus:border-accent-primary focus:ring-1 focus:ring-accent-primary"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium text-text-secondary">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  setAuthError('');
                }}
                required
                className="h-8 px-3 bg-search-bg border-border text-text-primary placeholder-text-secondary focus:border-accent-primary focus:ring-1 focus:ring-accent-primary"
              />
              {authError ? (
                <div className="flex items-start gap-2 mt-2 text-error">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <p className="text-sm font-medium">{authError}</p>
                </div>
              ) : (
                <p className="text-xs text-text-secondary mt-1">
                  Password must be at least 12 characters long and contain lowercase, uppercase, numbers, and special characters.
                </p>
              )}
            </div>
            {TURNSTILE_ENABLED && (
              <div className="flex justify-center">
                <Turnstile
                  siteKey={import.meta.env.VITE_TURNSTILE_SITE_KEY}
                  onSuccess={isSignUp ? setSignUpCaptchaToken : setSignInCaptchaToken}
                  options={{
                    theme: document.documentElement.classList.contains('dark') ? 'dark' : 'light'
                  }}
                />
              </div>
            )}
            <Button 
              type="submit" 
              className="w-full h-8 px-4 font-semibold text-sm bg-accent-primary hover:bg-accent-hover text-button-text"
              disabled={loading || (TURNSTILE_ENABLED && ((isSignUp && !signUpCaptchaToken) || (!isSignUp && !signInCaptchaToken)))}
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Please wait...
                </span>
              ) : (
                isSignUp ? 'Sign up' : 'Sign in'
              )}
            </Button>
          </form>
        </div>

        <div className="border-t border-border bg-background-secondary p-6 text-center">
          <button
            type="button"
            className="text-sm text-accent-primary hover:text-accent-hover"
            onClick={() => {
              setIsSignUp(!isSignUp);
              setSignUpCaptchaToken('');
              setSignInCaptchaToken('');
              setAuthError('');
            }}
          >
            {isSignUp ? 'Already have an account? Sign in' : "Don't have an account? Sign up"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}